import get from 'lodash/get';
import isObject from 'lodash/isObject';

import { shuffleArray } from './';

export type AutocompleteOption<T> = T | Array<T>;

export interface AutocompleteOptionConfig {
  value?: string;
  label: string;
}

export function getOptions<T>(options?: Array<T>, randomize?: boolean) {
  let opts = options || [];
  if (randomize) {
    opts = shuffleArray(opts);
  }
  return opts;
}

export function getOptionFromConfig<T>(
  option: T | Array<T>,
  config?: AutocompleteOptionConfig,
  multiple?: boolean,
): AutocompleteOption<T> {
  // Preserve explicit clearing: return null directly so react-hook-form stores null (not undefined)
  if (option === null || option === undefined) {
    return null as any;
  }
  let opt: AutocompleteOption<T> = option;

  if (!config) {
    return opt;
  }

  if (config.value) {
    // This is to account for the quirky behaviour of onChange returning an array
    if (multiple && Array.isArray(option)) {
      const value: Array<T> = [];
      for (const item of option) {
        if (isObject(item)) {
          value.push(get(item, config.value));
        } else {
          value.push(item);
        }
      }
      opt = value;
    } else {
      // Only attempt path lookup if option is still non-null (guarded above)
      opt = get(option as any, config.value);
      // If path lookup failed, normalize undefined to null to keep field key with null value
      if (opt === undefined) {
        opt = null as any;
      }
    }
  }

  return opt;
}

/*
  Required to handle quirky behaviour of Autocomplete component
  where it returns the option object when opening the selection box
  and returns the option value upon selection
  */
export function getLabel<T>(option: T, options: Array<T>, config?: AutocompleteOptionConfig, multiple?: boolean) {
  // if not config, options must be primitive
  if (!config) {
    return String(option);
  }
  if (isObject(option)) {
    return String(get(option, config.label));
  }
  // if option is not an object, get option by value
  // then get the label based on config
  if (config?.value) {
    const o = options.find(opt => getOptionFromConfig(opt, config, multiple) === option);
    return String(o ? get(o, config.label) : option);
  }

  // if all else fails, return option as string
  return String(option || '');
}
